import type {
  Budget,
  BudgetSummary,
  Transaction,
  TransactionSummary,
  PageResponse,
  CreateBudgetRequest,
  UpdateBudgetRequest,
  CopyBudgetRequest,
  CreateTransactionRequest,
  UpdateTransactionRequest,
  CreateShareRequest,
  BudgetShare,
  ShareInvitation,
  UnassignedSpendingListResponse,
  UnassignedSpendingAssignRequest,
  UnassignedSpendingAssignResponse,
  FxRate,
  FxConversionResponse,
  FxRateHistory,
  SyncRequest,
  SyncResponse,
  ApiError,
  Tag,
  ApiKey,
  ApiKeyPermissionGroup,
  MerchantSuggestion,
  UserProfile,
  ClassificationFeedbackRequest,
  ClassificationSettings,
} from '@/types';

const API_BASE = (() => {
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
})();

/** In-memory access token — never persist after migration. */
let memoryAccessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;
/** When true (PWA lock screen), do not mint a new access token from the refresh cookie. */
let refreshBlocked = false;

export function setAccessToken(token: string | null): void {
  memoryAccessToken = token;
}

export function getAccessToken(): string | null {
  return memoryAccessToken;
}

export function setRefreshBlocked(blocked: boolean): void {
  refreshBlocked = blocked;
}

/** One-time migration from legacy localStorage token storage. */
export function migrateAccessTokenFromStorage(): string | null {
  try {
    const legacy = localStorage.getItem('accessToken');
    if (legacy) {
      memoryAccessToken = legacy;
      localStorage.removeItem('accessToken');
      return legacy;
    }
  } catch {
    /* ignore */
  }
  return memoryAccessToken;
}

export function clearLegacyAccessTokenStorage(): void {
  try {
    localStorage.removeItem('accessToken');
  } catch {
    /* ignore */
  }
}

// Global handler for 401 errors (after refresh attempt fails)
let handleUnauthorized: ((reason: string, message: string) => void) | null = null;

export function setUnauthorizedHandler(handler: (reason: string, message: string) => void) {
  handleUnauthorized = handler;
}

async function tryRefreshAccessToken(): Promise<boolean> {
  if (refreshBlocked) return false;
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${API_BASE}/v1/oauth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) return false;
      const data = (await response.json().catch(() => null)) as { accessToken?: string } | null;
      if (!data?.accessToken) return false;
      memoryAccessToken = data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * Fetch wrapper with error handling, credentials cookie refresh, and auth.
 */
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit & { skipUnauthorizedHandler?: boolean } = {},
  retried = false
): Promise<T> {
  const token = memoryAccessToken;
  const { skipUnauthorizedHandler, ...requestInit } = options;

  const response = await fetch(`${API_BASE}/v1${endpoint}`, {
    ...requestInit,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...requestInit.headers,
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      if (!retried && !refreshBlocked) {
        const refreshed = await tryRefreshAccessToken();
        if (refreshed) {
          return fetchApi<T>(endpoint, options, true);
        }
      }

      const error: ApiError = await response.json().catch(() => ({
        code: 'UNAUTHORIZED',
        message: 'Authentication required',
        timestamp: new Date().toISOString(),
      }));

      if (handleUnauthorized && !skipUnauthorizedHandler) {
        const reason =
          (error as ApiError & { error?: string }).error ||
          error.code ||
          'authentication_failed';
        const message = error.message || 'Authentication required. Please log in.';
        handleUnauthorized(reason, message);
      }

      throw new ApiException(error, response.status);
    }

    const error: ApiError = await response.json().catch(() => ({
      code: 'NETWORK_ERROR',
      message: 'Network request failed',
      timestamp: new Date().toISOString(),
    }));
    throw new ApiException(error, response.status);
  }

  // 204/205 never have a body. 201/202 (and others) may also be empty — e.g. push
  // subscribe (201) and send-test (202). Parsing "" as JSON throws a SyntaxError;
  // Safari reports it as "The string did not match the expected pattern."
  if (response.status === 204 || response.status === 205) {
    return undefined as T;
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

export class ApiException extends Error {
  constructor(
    public readonly error: ApiError,
    public readonly status: number
  ) {
    super(error.message);
    this.name = 'ApiException';
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

// ===== API Key API =====

export const apiKeyApi = {
  async listPermissions(): Promise<ApiKeyPermissionGroup[]> {
    return fetchApi('/apikeys/permissions');
  },
  async list(): Promise<ApiKey[]> {
    return fetchApi('/apikeys');
  },
  async create(name: string, permissions: string[]): Promise<ApiKey> {
    return fetchApi('/apikeys', {
      method: 'POST',
      body: JSON.stringify({ name, permissions }),
    });
  },
  async update(id: string, data: { name?: string; permissions?: string[] }): Promise<ApiKey> {
    return fetchApi(`/apikeys/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  async remove(id: string): Promise<void> {
    return fetchApi(`/apikeys/${id}`, {
      method: 'DELETE',
    });
  },
};

// ===== Budget API =====

export const budgetApi = {
  async getAll(): Promise<Budget[]> {
    return fetchApi('/budgets');
  },

  async getActive(): Promise<Budget[]> {
    return fetchApi('/budgets/active');
  },

  async getSummariesPage(
    options: { activeOnly?: boolean; page?: number; size?: number } = {}
  ): Promise<PageResponse<BudgetSummary>> {
    const params = new URLSearchParams();
    if (options.activeOnly) params.set('activeOnly', 'true');
    params.set('page', String(options.page ?? 0));
    params.set('size', String(options.size ?? 20));
    return fetchApi(`/budgets/summaries?${params.toString()}`);
  },

  async getSummaries(): Promise<BudgetSummary[]> {
    const response = await this.getSummariesPage({ page: 0, size: 100 });
    return response.content;
  },

  async getActiveSummaries(): Promise<BudgetSummary[]> {
    return fetchApi('/budgets/summaries/active');
  },

  async getById(id: string): Promise<Budget> {
    return fetchApi(`/budgets/${id}`);
  },

  async getTransactions(
    budgetId: string,
    options: { includeDescendants?: boolean; page?: number; size?: number } = {}
  ): Promise<PageResponse<TransactionSummary>> {
    const params = new URLSearchParams();
    if (options.includeDescendants) params.set('includeDescendants', 'true');
    params.set('page', String(options.page ?? 0));
    params.set('size', String(options.size ?? 100));
    const query = params.toString();
    return fetchApi(`/budgets/${budgetId}/transactions?${query}`);
  },

  async create(data: CreateBudgetRequest): Promise<Budget> {
    return fetchApi('/budgets', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async update(id: string, data: UpdateBudgetRequest): Promise<Budget> {
    return fetchApi(`/budgets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async delete(id: string, confirmation?: boolean): Promise<void> {
    const suffix = confirmation ? '?confirmation=true' : '';
    return fetchApi(`/budgets/${id}${suffix}`, { method: 'DELETE' });
  },

  async copy(data: CopyBudgetRequest): Promise<Budget> {
    return fetchApi('/budgets/copy', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// ===== Transaction API =====

export const transactionApi = {
  async suggestMerchants(q: string, limit = 10): Promise<MerchantSuggestion[]> {
    const params = new URLSearchParams({ q, limit: String(limit) });
    return fetchApi(`/transactions/merchants/suggest?${params}`);
  },

  async getByBudget(budgetId: string): Promise<Transaction[]> {
    return fetchApi(`/transactions/budget/${budgetId}`);
  },

  async getByDateRange(
    budgetId: string,
    startDate: string,
    endDate: string
  ): Promise<Transaction[]> {
    return fetchApi(
      `/transactions/budget/${budgetId}/range?startDate=${startDate}&endDate=${endDate}`
    );
  },

  async getById(id: string): Promise<Transaction> {
    return fetchApi(`/transactions/${id}`);
  },

  async create(data: CreateTransactionRequest): Promise<Transaction> {
    return fetchApi('/transactions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async update(id: string, data: UpdateTransactionRequest): Promise<Transaction> {
    return fetchApi(`/transactions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async delete(id: string): Promise<void> {
    return fetchApi(`/transactions/${id}`, { method: 'DELETE' });
  },

  async submitClassificationFeedback(
    id: string,
    data: ClassificationFeedbackRequest
  ): Promise<void> {
    return fetchApi(`/transactions/${id}/classification-feedback`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// ===== FX Rate API =====

export const fxRateApi = {
  async getLatest(from: string, to: string): Promise<FxRate> {
    return fetchApi(`/fx-rates/latest?from=${from}&to=${to}`);
  },

  async getAtDate(from: string, to: string, date: string): Promise<FxRate> {
    return fetchApi(`/fx-rates/at-date?from=${from}&to=${to}&date=${date}`);
  },

  async getHistory(
    from: string,
    to: string,
    startDate: string,
    endDate: string
  ): Promise<FxRateHistory> {
    return fetchApi(
      `/fx-rates/history?from=${from}&to=${to}&startDate=${startDate}&endDate=${endDate}`
    );
  },

  async convert(
    amount: number,
    fromCurrency: string,
    toCurrency: string,
    date?: string
  ): Promise<FxConversionResponse> {
    return fetchApi('/fx-rates/convert', {
      method: 'POST',
      body: JSON.stringify({ amount, fromCurrency, toCurrency, date }),
    });
  },
};

// ===== Sync API =====

export const syncApi = {
  async sync(request: SyncRequest): Promise<SyncResponse> {
    return fetchApi('/sync', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },
};

// ===== Share API =====

export const shareApi = {
  async create(data: CreateShareRequest): Promise<BudgetShare> {
    return fetchApi('/shares', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getByBudget(budgetId: string): Promise<BudgetShare[]> {
    return fetchApi(`/shares/budget/${budgetId}`);
  },

  async getInvitations(): Promise<ShareInvitation[]> {
    return fetchApi('/shares/invitations');
  },

  async accept(shareId: string): Promise<BudgetShare> {
    return fetchApi(`/shares/${shareId}/accept`, { method: 'POST' });
  },

  async updateRole(shareId: string, role: string): Promise<BudgetShare> {
    return fetchApi(`/shares/${shareId}`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  },

  async delete(shareId: string): Promise<void> {
    return fetchApi(`/shares/${shareId}`, { method: 'DELETE' });
  },
};

// ===== Unassigned Spending API =====

export const unassignedSpendingApi = {
  async list(): Promise<UnassignedSpendingListResponse> {
    return fetchApi('/unassigned-spending');
  },

  async assign(data: UnassignedSpendingAssignRequest): Promise<UnassignedSpendingAssignResponse> {
    return fetchApi('/unassigned-spending/assign', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async discard(ids: string[]): Promise<void> {
    return fetchApi('/unassigned-spending', {
      method: 'DELETE',
      body: JSON.stringify({ ids }),
    });
  },
};

// ===== Tag API =====

export const tagApi = {
  async list(): Promise<Tag[]> {
    return fetchApi('/tags');
  },

  async create(name: string): Promise<Tag> {
    return fetchApi('/tags', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  async rename(id: string, name: string): Promise<Tag> {
    return fetchApi(`/tags/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  },

  async remove(id: string): Promise<void> {
    return fetchApi(`/tags/${id}`, { method: 'DELETE' });
  },

  async attachToTransaction(transactionId: string, tagId: string): Promise<void> {
    return fetchApi(`/tags/transactions/${transactionId}/attach/${tagId}`, {
      method: 'POST',
    });
  },

  async detachFromTransaction(transactionId: string, tagId: string): Promise<void> {
    return fetchApi(`/tags/transactions/${transactionId}/detach/${tagId}`, {
      method: 'DELETE',
    });
  },
};

export const authApi = {
  async getMe(): Promise<UserProfile & { id?: string; admin?: boolean }> {
    const me = await fetchApi<UserProfile & { id?: string; admin?: boolean }>('/oauth/me');
    return {
      ...me,
      userId: me.userId ?? me.id ?? '',
      isAdmin: me.isAdmin ?? me.admin ?? false,
    };
  },

  async deleteAccount(): Promise<void> {
    await fetchApi('/users/me', { method: 'DELETE' });
  },

  async updateName(name: string): Promise<UserProfile> {
    return fetchApi('/users/me', {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
  },

  /** Exchange one-time OAuth session code (?session=) — body field is `code`. */
  async exchangeSession(code: string): Promise<SessionTokenResponse> {
    return fetchApi('/oauth/session/exchange', {
      method: 'POST',
      body: JSON.stringify({ code }),
      skipUnauthorizedHandler: true,
    });
  },

  async refresh(): Promise<{ accessToken: string }> {
    return fetchApi('/oauth/refresh', {
      method: 'POST',
      skipUnauthorizedHandler: true,
    });
  },

  async logoutSession(): Promise<void> {
    await fetchApi('/oauth/logout-session', {
      method: 'POST',
      skipUnauthorizedHandler: true,
    });
  },
};

export interface SessionUserSnapshot {
  userId: string;
  email?: string;
  username?: string;
  displayName?: string;
  name?: string;
  provider?: string;
  providerManaged?: boolean;
  isAdmin?: boolean;
}

export interface SessionTokenResponse {
  accessToken: string;
  user?: SessionUserSnapshot;
  offlineCapable?: boolean;
}

export interface WebAuthnCredentialDto {
  id: string;
  deviceName?: string;
  credentialId?: string;
  createdAt?: string;
  lastUsedAt?: string | null;
  transports?: string;
}

export const webauthnApi = {
  async registerOptions(): Promise<import('@/lib/webauthn').PublicKeyCredentialCreationOptionsJSON> {
    return fetchApi('/webauthn/register/options', { method: 'POST', body: '{}' });
  },

  async register(
    attestation: import('@/lib/webauthn').RegistrationCredentialJSON,
    deviceName?: string
  ): Promise<WebAuthnCredentialDto> {
    return fetchApi('/webauthn/register', {
      method: 'POST',
      body: JSON.stringify({ attestation, deviceName }),
    });
  },

  async loginOptions(
    credentialId?: string
  ): Promise<import('@/lib/webauthn').PublicKeyCredentialRequestOptionsJSON> {
    return fetchApi('/webauthn/login/options', {
      method: 'POST',
      body: JSON.stringify(credentialId ? { credentialId } : {}),
      skipUnauthorizedHandler: true,
    });
  },

  async login(
    assertion: import('@/lib/webauthn').AuthenticationCredentialJSON
  ): Promise<SessionTokenResponse> {
    return fetchApi('/webauthn/login', {
      method: 'POST',
      body: JSON.stringify({ assertion }),
      skipUnauthorizedHandler: true,
    });
  },

  async listCredentials(): Promise<WebAuthnCredentialDto[]> {
    return fetchApi('/webauthn/credentials');
  },

  async updateCredential(
    id: string,
    data: { deviceName: string }
  ): Promise<WebAuthnCredentialDto> {
    return fetchApi(`/webauthn/credentials/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteCredential(id: string): Promise<void> {
    await fetchApi(`/webauthn/credentials/${id}`, { method: 'DELETE' });
  },
};

// ===== Web Push API =====

export const pushApi = {
  async getVapidPublicKey(): Promise<{ publicKey: string }> {
    return fetchApi('/push/vapid-public-key');
  },

  async subscribe(data: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
    userAgent?: string;
    platform?: 'IOS' | 'ANDROID' | 'OTHER';
  }): Promise<void> {
    await fetchApi('/push/subscriptions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async unsubscribe(endpoint: string): Promise<void> {
    await fetchApi('/push/subscriptions', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    });
  },

  async sendTest(): Promise<void> {
    await fetchApi('/push/test', { method: 'POST' });
  },

  async getPreferences(): Promise<{
    pushInvitationsEnabled: boolean;
    pushSharedSpendingEnabled: boolean;
    hasSubscription: boolean;
  }> {
    return fetchApi('/push/preferences');
  },

  async updatePreferences(data: {
    pushInvitationsEnabled?: boolean;
    pushSharedSpendingEnabled?: boolean;
  }): Promise<{
    pushInvitationsEnabled: boolean;
    pushSharedSpendingEnabled: boolean;
    hasSubscription: boolean;
  }> {
    return fetchApi('/push/preferences', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

export const adminMailApi = {
  async getSettings(): Promise<import('@/types').MailSettings> {
    return fetchApi('/admin/mail-settings');
  },

  async updateSettings(data: import('@/types').MailSettingsUpdate): Promise<import('@/types').MailSettings> {
    return fetchApi('/admin/mail-settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async sendTest(toEmail?: string): Promise<void> {
    await fetchApi('/admin/mail-settings/test', {
      method: 'POST',
      body: JSON.stringify(toEmail ? { toEmail } : {}),
    });
  },
};

export type AdminPushRecipient = {
  userId: string;
  email: string | null;
  displayName: string | null;
  name: string | null;
  subscriptionCount: number;
};

export type AdminPushSendResult = {
  requestedUserCount: number;
  subscriptionCount: number;
};

export const adminClassificationApi = {
  async getSettings(): Promise<ClassificationSettings> {
    return fetchApi('/admin/classification-settings');
  },

  async updateSettings(data: ClassificationSettings): Promise<ClassificationSettings> {
    return fetchApi('/admin/classification-settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

export const adminPushApi = {
  async listRecipients(): Promise<AdminPushRecipient[]> {
    return fetchApi('/admin/push/recipients');
  },

  async send(data: {
    userIds: string[];
    title: string;
    body: string;
    url?: string;
  }): Promise<AdminPushSendResult> {
    return fetchApi('/admin/push/send', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async sendClassificationTest(data: {
    userIds: string[];
    transactionId: string;
    merchant?: string;
    budgetName?: string;
  }): Promise<AdminPushSendResult> {
    return fetchApi('/admin/push/send-classification-test', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};
